/**
 * fullReportGenerator.ts
 * Generates a complete, professional HTML report (PDF-ready) and a PowerPoint PPTX file
 * from the combined data of ActionRecommendationsMasterAI + ResultsMaster.
 *
 * Structure:
 *  PART A — Handlungsempfehlungen (comes first)
 *    1. Cover / Executive Summary
 *    2. Executive Diagnose
 *    3. Top-3-Prioritäten (mit Maßnahmen)
 *    4. Quick Wins (14 Tage)
 *    5. 90-Tage-Fahrplan
 *    6. Operative Zielgrößen je Rep
 *
 *  PART B — Audit-Ergebnisse
 *    7. Umsatzrealität (Funnel, Revenue Gap, Hebel)
 *    8. Strukturelle Ursachen
 *    9. Team-Performance
 *    10. Funnel Conversion Rates
 *    11. Operativer Zielpfad
 */

import type { MasterResult } from '@/lib/master-engine';

// ─── Helpers ───────────────────────────────────────────────────────────────────
function fmtEur(v: number | null | undefined): string {
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(Number.isFinite(Number(v)) ? Number(v) : 0);
}

function fmtNum(v: number | null | undefined, decimals = 1): string {
  if (v === null || v === undefined || !Number.isFinite(Number(v))) return '–';
  return Number(v).toFixed(decimals).replace('.', ',');
}

function fmtPct(v: number | null | undefined, decimals = 1): string {
  if (v === null || v === undefined || !Number.isFinite(Number(v))) return '–';
  return Number(v).toFixed(decimals).replace('.', ',') + '%';
}

function safeArr<T>(x: T[] | null | undefined): T[] {
  return Array.isArray(x) ? x : [];
}

function today(): string {
  return new Date().toLocaleDateString('de-DE', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

// ─── Types for already-computed UI data ────────────────────────────────────────
export interface ReportPriorityCard {
  rank: number;
  title: string;
  problem: string;
  rootCause: string;
  shortTermImpact: string;
  actions: string[];
  kpiContext?: string;
  revenueLeakMonthly?: number;
  taskDetails?: Array<{
    title: string;
    description: string;
    steps: string[];
    owner: string;
    dueInDays: number;
    successMetric: string;
    effort?: string;
    impact?: string;
  }>;
  enrichedActions?: string[];
}

export interface ReportQuickWin {
  title: string;
  whyItWorks: string;
  implementation: string;
  owner: string;
  deadline: string;
  successMetric: string;
  expectedImpact?: string;
}

export interface ReportRoadmap {
  phase_1_0_30: { title: string; goal: string; focus: string; actions: string[] };
  phase_2_30_60: { title: string; goal: string; focus: string; actions: string[] };
  phase_3_60_90: { title: string; goal: string; focus: string; actions: string[] };
}

export interface ReportRec {
  rank: number;
  category: string;
  title: string;
  urgencyLabel: string;
  urgencyValue: string;
  timeframe: string;
  timeframePhase: string;
  headline: string;
  situationText: string;
  rootCauseText: string;
  consequenceText: string;
  kpiContext: string;
  measures: Array<{ title: string; body: string; lever: string }>;
  successCriteria: Array<{ metric: string; target: string }>;
  whyNow: string;
  impactStatement: string;
  firstStep: string;
}

export interface FullReportData {
  result: MasterResult;
  priorities: ReportPriorityCard[];
  quickWins: ReportQuickWin[];
  roadmap: ReportRoadmap;
  executiveDiagnosis: { headline: string; summary: string };
  signatureSentence: string;
  top5Recs?: ReportRec[];
}

// ═══════════════════════════════════════════════════════════════════════════════
// HTML REPORT GENERATOR
// ═══════════════════════════════════════════════════════════════════════════════

function buildStyles(): string {
  return `
    @page { size: A4 landscape; margin: 0; }
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif;
      font-size: 10.5pt;
      color: #0f172a;
      line-height: 1.6;
      background: #f1f5f9;
      word-wrap: break-word; overflow-wrap: break-word;
    }
    @media print {
      * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
      body { margin: 0; padding: 0; background: #fff; }
      .no-print { display: none !important; }
    }

    /* Slide Container */
    .slide {
      width: 297mm;
      min-height: 210mm;
      background: #fff;
      margin: 0 auto 20px;
      position: relative;
      page-break-after: always;
      box-shadow: 0 4px 12px rgba(0,0,0,0.1);
    }
    @media print {
      .slide { margin: 0; box-shadow: none; page-break-after: always; height: auto; }
    }
    .slide-content {
      padding: 15mm 20mm;
      min-height: 100%;
      display: block;
    }

    /* ── Typography ── */
    h1 { font-size: 26pt; font-weight: 900; color: #0f172a; line-height: 1.1; margin-bottom: 10px; }
    h2 { font-size: 14pt; font-weight: 800; color: #fff; background: linear-gradient(135deg,#0f172a,#1e3a8a); padding: 14px 20px; border-radius: 10px; margin: 0 0 14px; border-bottom: none; }
    h3 { font-size: 11pt; font-weight: 700; color: #0f172a; margin: 14px 0 6px; }
    h4 { font-size: 9.5pt; font-weight: 700; color: #334155; margin: 10px 0 4px; }
    p  { margin-bottom: 8px; color: #334155; font-size: 8.5pt; line-height: 1.6; }

    /* ── Toolbar ── */
    .toolbar {
      position: fixed; top: 0; left: 0; right: 0; z-index: 999;
      background: #0f172a; padding: 10px 24px;
      display: flex; align-items: center; justify-content: space-between;
      box-shadow: 0 2px 8px rgba(0,0,0,0.3);
    }
    .toolbar-label { color: rgba(255,255,255,0.65); font-size: 12px; font-weight: 600; }
    .toolbar-btns  { display: flex; gap: 10px; }
    .btn-print {
      background: #1d4ed8; color: #fff; border: none;
      padding: 8px 18px; border-radius: 7px; font-size: 12px; font-weight: 700; cursor: pointer;
    }
    .btn-close {
      background: #475569; color: #fff; border: none;
      padding: 8px 14px; border-radius: 7px; font-size: 12px; font-weight: 700; cursor: pointer;
    }
    .toolbar-spacer { height: 50px; }

    /* ── Cover ── */
    .cover {
      width: 297mm;
      height: 210mm;
      background: linear-gradient(135deg, #0f172a 0%, #1e3a8a 60%, #1d4ed8 100%);
      color: #fff;
      padding: 50px 48px 40px;
      position: relative;
      overflow: hidden;
      page-break-after: always;
      display: flex; flex-direction: column; justify-content: center;
      margin: 0 auto 20px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.1);
    }
    @media print {
      .cover { margin: 0; box-shadow: none; }
    }
    .cover::before {
      content: '';
      position: absolute;
      top: -80px; right: -80px;
      width: 320px; height: 320px;
      border-radius: 50%;
      background: rgba(59,130,246,0.15);
    }
    .cover::after {
      content: '';
      position: absolute;
      bottom: -60px; left: -60px;
      width: 240px; height: 240px;
      border-radius: 50%;
      background: rgba(220,38,38,0.1);
    }
    .cover-logo { font-size: 13pt; font-weight: 900; letter-spacing: 4px; color: rgba(255,255,255,0.5); margin-bottom: 50px; }
    .cover-badge {
      display: inline-flex; align-items: center; gap: 8px;
      background: rgba(220,38,38,0.2); border: 1px solid rgba(220,38,38,0.4);
      padding: 5px 14px; border-radius: 999px;
      font-size: 8pt; font-weight: 700; color: #fca5a5; letter-spacing: 1px;
      text-transform: uppercase; margin-bottom: 22px;
    }
    .cover-dot {
      width: 8px; height: 8px; border-radius: 50%; background: #ef4444;
      box-shadow: 0 0 0 3px rgba(239,68,68,0.3);
    }
    .cover h1 { font-size: 34pt; color: #fff; margin-bottom: 8px; }
    .cover-sub { font-size: 14pt; color: rgba(255,255,255,0.65); margin-bottom: 30px; }
    .cover-meta { font-size: 10pt; color: rgba(255,255,255,0.45); }
    .cover-kpi-row {
      display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; margin-top: 32px;
    }
    .cover-kpi {
      background: rgba(255,255,255,0.08); border: 1px solid rgba(255,255,255,0.12);
      border-radius: 12px; padding: 16px;
    }
    .cover-kpi-label { font-size: 7.5pt; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; color: rgba(255,255,255,0.45); margin-bottom: 6px; }
    .cover-kpi-value { font-size: 18pt; font-weight: 900; color: #fff; line-height: 1; }
    .cover-kpi-sub { font-size: 8pt; color: rgba(255,255,255,0.4); margin-top: 4px; }

    /* ── Section wrappers ── */
    .section { padding: 20px 0; }
    .section + .section { border-top: 1px solid #f1f5f9; }

    /* ── Part divider ── */
    .part-divider {
      background: linear-gradient(135deg, #0f172a, #1e3a8a);
      color: #fff;
      padding: 22px 28px;
      border-radius: 14px;
      margin: 10px 0 24px;
    }
    .part-divider-label { font-size: 7.5pt; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; color: rgba(255,255,255,0.4); margin-bottom: 5px; }
    .part-divider h2 { font-size: 15pt; color: #fff; background: none; border: none; margin: 0; padding: 0; border-radius: 0; }

    /* ── KPI Grid ── */
    .kpi-grid-3 { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; margin: 14px 0; }
    .kpi-grid-4 { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin: 14px 0; }
    .kpi-grid-2 { display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px; margin: 14px 0; }
    .kpi-card {
      background: #f8fafc; border: 1.5px solid #e2e8f0; border-radius: 10px; padding: 14px;
    }
    .kpi-label { font-size: 7.5pt; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; color: #64748b; margin-bottom: 5px; }
    .kpi-value { font-size: 20pt; font-weight: 900; color: #0f172a; line-height: 1.1; }
    .kpi-value.red { color: #dc2626; }
    .kpi-value.blue { color: #1d4ed8; }
    .kpi-value.green { color: #16a34a; }
    .kpi-value.amber { color: #d97706; }
    .kpi-value.small { font-size: 12pt; }
    .kpi-sub { font-size: 8pt; color: #64748b; margin-top: 3px; }

    /* ── Priority cards ── */
    .priority-card {
      border: 1.5px solid #e2e8f0; border-radius: 14px; padding: 20px; margin-bottom: 14px;
    }
    .priority-card.dominant {
      border: 2.5px solid #1d4ed8;
      background: linear-gradient(135deg, #eff6ff, #f0f9ff, #eff6ff);
    }
    .priority-rank-badge {
      display: inline-flex; align-items: center; justify-content: center;
      width: 28px; height: 28px; border-radius: 50%;
      background: #1e3a8a; color: #fff;
      font-size: 11pt; font-weight: 900; margin-bottom: 8px;
    }
    .priority-rank-badge.rank-1 { background: #dc2626; }
    .priority-rank-badge.rank-2 { background: #d97706; }
    .priority-rank-badge.rank-3 { background: #059669; }
    .priority-title { font-size: 13pt; font-weight: 800; color: #0f172a; margin-bottom: 6px; }
    .priority-kpi {
      display: inline-flex; align-items: center; gap: 6px;
      background: #f1f5f9; border: 1px solid #e2e8f0;
      padding: 3px 10px; border-radius: 6px;
      font-size: 8pt; font-weight: 700; color: #475569; margin-bottom: 10px;
    }
    .priority-block { margin-top: 10px; }
    .priority-block-label {
      font-size: 7.5pt; font-weight: 800; text-transform: uppercase; letter-spacing: 1px;
      color: #64748b; margin-bottom: 4px;
    }
    .priority-block-text { font-size: 9pt; color: #334155; line-height: 1.55; }

    .task-card {
      background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 10px;
      padding: 14px; margin-top: 10px;
    }
    .task-title { font-size: 10pt; font-weight: 700; color: #1e3a8a; margin-bottom: 4px; }
    .task-desc { font-size: 8.5pt; color: #475569; margin-bottom: 8px; line-height: 1.5; }
    .task-meta { display: flex; gap: 12px; flex-wrap: wrap; margin-top: 8px; }
    .task-meta-item { font-size: 7.5pt; color: #64748b; }
    .task-meta-item strong { color: #334155; }
    .steps-list { padding-left: 0; list-style: none; margin-top: 8px; }
    .steps-list li {
      display: flex; align-items: flex-start; gap: 8px;
      font-size: 8.5pt; color: #334155; margin-bottom: 5px; line-height: 1.5;
    }
    .step-num {
      flex-shrink: 0; width: 20px; height: 20px; border-radius: 50%;
      background: #1d4ed8; color: #fff;
      font-size: 7.5pt; font-weight: 700;
      display: flex; align-items: center; justify-content: center;
      margin-top: 1px;
    }
    .step-num.s2 { background: #d97706; }
    .step-num.s3 { background: #059669; }

    /* ── Quick Wins ── */
    .qw-card {
      border: 1.5px solid #d1fae5; background: #f0fdf4;
      border-radius: 12px; padding: 16px; margin-bottom: 12px;
    }
    .qw-title { font-size: 11pt; font-weight: 800; color: #065f46; margin-bottom: 5px; }
    .qw-badge {
      display: inline-block; background: #16a34a; color: #fff;
      font-size: 7.5pt; font-weight: 700; padding: 3px 10px; border-radius: 999px;
      margin-bottom: 8px; text-transform: uppercase; letter-spacing: 0.5px;
    }
    .qw-section-label {
      font-size: 7.5pt; font-weight: 800; text-transform: uppercase; letter-spacing: 1px;
      color: #064e3b; margin-bottom: 3px; margin-top: 8px;
    }
    .qw-text { font-size: 9pt; color: #065f46; line-height: 1.55; }
    .qw-impact {
      background: #dcfce7; border: 1px solid #bbf7d0; border-radius: 8px; padding: 8px 12px; margin-top: 8px;
      font-size: 9pt; color: #14532d; font-weight: 600;
    }
    .qw-meta { display: flex; gap: 16px; flex-wrap: wrap; margin-top: 8px; }
    .qw-meta-item { font-size: 8pt; color: #475569; }
    .qw-meta-item strong { color: #0f172a; }

    /* ── Roadmap ── */
    .roadmap-phase {
      border-radius: 12px; padding: 18px; margin-bottom: 12px;
    }
    .phase-0-30 { background: #fef2f2; border: 1.5px solid #fca5a5; }
    .phase-30-60 { background: #fffbeb; border: 1.5px solid #fcd34d; }
    .phase-60-90 { background: #f0fdf4; border: 1.5px solid #86efac; }
    .phase-header { display: flex; align-items: center; gap: 12px; margin-bottom: 8px; flex-wrap: wrap; }
    .phase-title { font-size: 13pt; font-weight: 800; color: #0f172a; }
    .phase-label-pill {
      font-size: 8pt; font-weight: 700; padding: 3px 10px; border-radius: 999px;
      text-transform: uppercase; letter-spacing: 0.5px;
    }
    .pill-red { background: #fecaca; color: #7f1d1d; }
    .pill-amber { background: #fef3c7; color: #78350f; border: 1px solid #fcd34d; }
    .pill-green { background: #dcfce7; color: #14532d; }
    .phase-goal { font-size: 10pt; font-weight: 700; color: #1e3a8a; margin-bottom: 6px; }
    .phase-focus {
      font-size: 8pt; color: #64748b; font-style: italic; margin-bottom: 8px;
    }
    .phase-actions { padding-left: 0; list-style: none; margin-top: 6px; }
    .phase-actions li {
      display: flex; align-items: flex-start; gap: 8px;
      font-size: 9pt; color: #334155; margin-bottom: 5px; line-height: 1.5;
    }
    .action-bullet {
      flex-shrink: 0; width: 6px; height: 6px; border-radius: 50%;
      background: #475569; margin-top: 6px;
    }

    /* ── Goal Path ── */
    .goalpath-grid {
      display: grid; grid-template-columns: 1fr 1fr; gap: 0;
      border: 1.5px solid #e2e8f0; border-radius: 12px; overflow: hidden; margin: 14px 0;
    }
    .goalpath-col { padding: 16px; }
    .goalpath-col + .goalpath-col { border-left: 1.5px solid #e2e8f0; }
    .goalpath-col-header {
      font-size: 8pt; font-weight: 800; text-transform: uppercase; letter-spacing: 1px;
      color: #64748b; margin-bottom: 12px;
    }
    .goalpath-row { display: flex; justify-content: space-between; align-items: center; padding: 8px 0; border-bottom: 1px solid #f1f5f9; }
    .goalpath-row:last-child { border-bottom: none; }
    .goalpath-metric { font-size: 9pt; color: #475569; }
    .goalpath-value { font-size: 11pt; font-weight: 800; color: #0f172a; }

    /* ── Funnel table ── */
    .funnel-table { width: 100%; border-collapse: collapse; margin: 14px 0; font-size: 9pt; }
    .funnel-table th { background: #1e3a8a; color: #fff; padding: 9px 12px; text-align: left; font-size: 8pt; font-weight: 700; }
    .funnel-table td { padding: 9px 12px; border-bottom: 1px solid #f1f5f9; }
    .funnel-table tr:nth-child(even) td { background: #f8fafc; }
    .funnel-table tr.total td { background: #eff6ff; font-weight: 700; }
    .status-ok { color: #16a34a; font-weight: 700; }
    .status-warn { color: #d97706; font-weight: 700; }
    .status-crit { color: #dc2626; font-weight: 700; }

    /* ── Structural causes ── */
    .cause-card { border-radius: 10px; padding: 14px; margin-bottom: 10px; }
    .cause-high { background: #fef2f2; border: 1.5px solid #fca5a5; }
    .cause-medium { background: #fffbeb; border: 1.5px solid #fcd34d; }
    .cause-good { background: #f0fdf4; border: 1.5px solid #86efac; }
    .cause-header { display: flex; justify-content: space-between; align-items: flex-start; flex-wrap: wrap; gap: 6px; }
    .cause-title { font-size: 10pt; font-weight: 700; color: #0f172a; }
    .cause-severity {
      font-size: 7.5pt; font-weight: 700; padding: 3px 8px; border-radius: 999px; text-transform: uppercase;
    }
    .sev-high { background: #fecaca; color: #7f1d1d; }
    .sev-medium { background: #fef3c7; color: #78350f; }
    .sev-good { background: #d1fae5; color: #065f46; }
    .cause-summary { font-size: 9pt; color: #334155; margin-top: 6px; line-height: 1.5; }
    .cause-impact { font-size: 9pt; color: #64748b; margin-top: 4px; }

    /* ── Signal flags ── */
    .signal-flag { display: flex; align-items: flex-start; gap: 8px; padding: 8px 0; border-bottom: 1px solid #f1f5f9; }
    .signal-flag:last-child { border-bottom: none; }
    .signal-icon { font-size: 11pt; flex-shrink: 0; }
    .signal-text { font-size: 9pt; color: #334155; }

    /* ── Footer ── */
    .report-footer {
      margin-top: 20px; padding: 16px 0 0; border-top: 1px solid #e2e8f0;
      font-size: 8pt; color: #94a3b8; text-align: center;
    }

    /* ── Executive diagnosis ── */
    .exec-box {
      background: linear-gradient(135deg, #fef9c3, #fffbeb);
      border: 2px solid #fcd34d; border-radius: 14px; padding: 20px; margin: 14px 0;
    }
    .exec-headline { font-size: 12pt; font-weight: 800; color: #78350f; margin-bottom: 8px; line-height: 1.35; }
    .exec-summary { font-size: 9.5pt; color: #92400e; line-height: 1.6; }
    .exec-signature {
      background: #0f172a; border-radius: 10px; padding: 14px 18px; margin-top: 14px;
      font-size: 10pt; font-weight: 600; color: #e2e8f0; font-style: italic; line-height: 1.5;
    }
  `;
}

// ─── Cover Page ────────────────────────────────────────────────────────────────
function buildCover(d: FullReportData): string {
  const r = d.result;
  const companyName = r.normalizedData?.companyName || 'Ihr Unternehmen';
  const annualGap =
    (r.teamPerformance?.projectedYearEndGap || 0) > 0
      ? r.teamPerformance!.projectedYearEndGap
      : r.computedMetrics?.annualRevenueGap || 0;
  const monthlyLoss =
    annualGap > 0
      ? Math.round(annualGap / 12)
      : r.computedMetrics?.monthlyRevenueGap || 0;
  const topLeverTitle = safeArr(r.topLevers)[0]?.title || '–';
  const reps = r.teamPerformance?.productiveReps || r.normalizedData?.teamSize || '–';

  return `
  <div class="cover">
    <div class="cover-logo">SALESFORGE</div>
    <div class="cover-badge"><div class="cover-dot"></div>Handlungsempfehlung &amp; Maßnahmenplan · Vertraulich</div>
    <h1 style="font-size:28pt;color:#fff;margin:0 0 8px;">Sales-Maßnahmen&shy;plan</h1>
    <div class="cover-sub">${companyName}</div>
    <div class="cover-meta">Erstellt am ${today()} · Nur für interne Verwendung · Powered by Salesforge</div>
    <div class="cover-kpi-row">
      <div class="cover-kpi">
        <div class="cover-kpi-label">Monatl. Potenzial</div>
        <div class="cover-kpi-value">${fmtEur(monthlyLoss)}</div>
        <div class="cover-kpi-sub">Identifiziertes Umsatzpotenzial/Mo.</div>
      </div>
      <div class="cover-kpi">
        <div class="cover-kpi-label">Jahres-Ziellücke</div>
        <div class="cover-kpi-value">${fmtEur(annualGap)}</div>
        <div class="cover-kpi-sub">Projizierter Gap bis Jahresende</div>
      </div>
      <div class="cover-kpi">
        <div class="cover-kpi-label">Engpass #1</div>
        <div class="cover-kpi-value" style="font-size:11pt;line-height:1.3;">${topLeverTitle}</div>
        <div class="cover-kpi-sub">Höchster Hebel</div>
      </div>
      <div class="cover-kpi">
        <div class="cover-kpi-label">Aktive Reps</div>
        <div class="cover-kpi-value">${reps}</div>
        <div class="cover-kpi-sub">Produktive Vertriebsmitarbeiter</div>
      </div>
    </div>
  </div>`;
}

// ─── Part A: Handlungsempfehlungen ─────────────────────────────────────────────
function buildPartA(d: FullReportData): string {
  const r = d.result;
  const companyName = r.normalizedData?.companyName || 'Ihr Unternehmen';
  let html = '';

  // ── 1. Executive-Diagnose ──
  html += `
  <div class="slide"><div class="slide-content">
    <div class="part-divider">
      <div class="part-divider-label">Teil A</div>
      <h2>Handlungsempfehlungen & Umsetzungsplan</h2>
    </div>
    <h2>1. Executive-Diagnose</h2>
    <div class="exec-box">
      <div class="exec-headline">${d.executiveDiagnosis.headline}</div>
      <div class="exec-summary">${d.executiveDiagnosis.summary}</div>
    </div>
    <div class="exec-signature">${d.signatureSentence}</div>
    <div class="report-footer">Salesforge Sales-Report · ${companyName} · ${today()}</div>
  </div></div>`;

  // ── 2. Top-3-Prioritäten ──
  d.priorities.forEach((p, i) => {
    const isDominant = i === 0;
    const rankColors = ['rank-1', 'rank-2', 'rank-3'];
    html += `
    <div class="slide"><div class="slide-content">
    <h2>2. Die 3 Engpass-Felder — Priorität ${p.rank}</h2>
    <div class="priority-card ${isDominant ? 'dominant' : ''}">
      <div class="priority-rank-badge ${rankColors[i]}">${p.rank}</div>
      <div class="priority-title">${p.title}</div>
      ${p.kpiContext ? `<div class="priority-kpi">${p.kpiContext}</div>` : ''}
      ${(p.revenueLeakMonthly || 0) > 0 ? `<div class="priority-kpi" style="color:#dc2626;border-color:#fca5a5;">Umsatzleck: ${fmtEur(p.revenueLeakMonthly)}/Monat</div>` : ''}

      <div class="priority-block">
        <div class="priority-block-label">Problem</div>
        <div class="priority-block-text">${p.problem}</div>
      </div>
      <div class="priority-block">
        <div class="priority-block-label">Root Cause / Was muss sich ändern</div>
        <div class="priority-block-text">${p.rootCause}</div>
      </div>
      <div class="priority-block">
        <div class="priority-block-label">Kurzfristiger Impact</div>
        <div class="priority-block-text">${p.shortTermImpact}</div>
      </div>

      ${buildTaskDetails(p, i)}
    </div>
    <div class="report-footer">Salesforge Sales-Report · ${companyName} · ${today()}</div>
    </div></div>`;
  });

  // ── 3. Quick Wins ──
  html += `
  <div class="slide"><div class="slide-content">
  <h2>3. Quick Wins — Sofort umsetzbar in 14 Tagen</h2>`;
  d.quickWins.forEach((qw, i) => {
    html += `
    <div class="qw-card">
      <div class="qw-badge">Quick Win ${i + 1}</div>
      <div class="qw-title">${qw.title}</div>
      <div class="qw-section-label">Warum das funktioniert</div>
      <div class="qw-text">${qw.whyItWorks}</div>
      <div class="qw-section-label">Konkrete Umsetzung</div>
      <div class="qw-text">${qw.implementation}</div>
      ${qw.expectedImpact ? `<div class="qw-impact">💡 Erwarteter Effekt: ${qw.expectedImpact}</div>` : ''}
      <div class="qw-meta">
        <div class="qw-meta-item"><strong>Owner:</strong> ${qw.owner}</div>
        <div class="qw-meta-item"><strong>Deadline:</strong> ${qw.deadline}</div>
        <div class="qw-meta-item"><strong>Erfolgsmetrik:</strong> ${qw.successMetric}</div>
      </div>
    </div>`;
  });
  html += `<div class="report-footer">Salesforge Sales-Report · ${companyName} · ${today()}</div></div></div>`;

  // ── 4. 90-Tage-Fahrplan ──
  const rm = d.roadmap;
  const phases = [
    { phase: rm.phase_1_0_30, cls: 'phase-0-30', pill: 'pill-red', label: 'Stabilisieren' },
    { phase: rm.phase_2_30_60, cls: 'phase-30-60', pill: 'pill-amber', label: 'Beschleunigen' },
    { phase: rm.phase_3_60_90, cls: 'phase-60-90', pill: 'pill-green', label: 'Skalieren' },
  ];
  html += `<div class="slide"><div class="slide-content"><h2>4. 90-Tage-Fahrplan</h2>`;
  phases.forEach(({ phase, cls, pill, label }) => {
    html += `
    <div class="roadmap-phase ${cls}">
      <div class="phase-header">
        <div class="phase-title">${phase.title}</div>
        <div class="phase-label-pill ${pill}">${label}</div>
      </div>
      <div class="phase-goal">🎯 Ziel: ${phase.goal}</div>
      <div class="phase-focus">Fokus: ${phase.focus}</div>
      <ul class="phase-actions">
        ${safeArr(phase.actions).map(a => `<li><div class="action-bullet"></div><span>${a}</span></li>`).join('')}
      </ul>
    </div>`;
  });
  html += `<div class="report-footer">Salesforge Sales-Report · ${companyName} · ${today()}</div></div></div>`;

  // ── 5. Operative Zielgrößen je Rep ──
  const gp = r.computedMetrics?.goalPath;
  const reps = r.teamPerformance?.productiveReps || r.normalizedData?.teamSize || 1;
  if (gp && gp.remainingRevenueNeeded > 0) {
    const teamContacts = gp.realisticRequiredContactsPerMonthFromNow || 0;
    const teamDiscovery = gp.realisticRequiredDiscoveryCallsPerMonthFromNow || 0;
    const teamPresentations = gp.realisticRequiredPresentationsPerMonthFromNow || 0;
    const teamDeals = gp.requiredDealsPerMonthFromNow || 0;
    const safeReps = Number(reps) > 0 ? Number(reps) : 1;
    const repContacts = Math.ceil(teamContacts / safeReps);
    const repDiscovery = Math.ceil(teamDiscovery / safeReps);
    const repPresentations = Math.ceil(teamPresentations / safeReps);
    const repDeals = (teamDeals / safeReps).toFixed(1);

    html += `
    <div class="slide"><div class="slide-content">
      <h2>5. Operative Zielgrößen — Was jetzt passieren muss</h2>
      <p style="margin-bottom:14px;font-size:9.5pt;color:#475569;">
        Basierend auf dem operativ belastbaren Zielpfad (audit-adjustierte Conversion-Raten).
        Verbleibender Bedarf: <strong>${fmtEur(gp.remainingRevenueNeeded)}</strong> in ca. <strong>${gp.monthsRemainingApprox} Monaten</strong>.
        Konfidenz: <strong>${gp.confidenceLabel || '–'}</strong>
      </p>
      <div class="goalpath-grid">
        <div class="goalpath-col">
          <div class="goalpath-col-header">Gesamt-Team / Monat</div>
          <div class="goalpath-row"><span class="goalpath-metric">Kontakte</span><span class="goalpath-value">${fmtNum(teamContacts, 0)}</span></div>
          <div class="goalpath-row"><span class="goalpath-metric">Discovery Calls</span><span class="goalpath-value">${fmtNum(teamDiscovery, 0)}</span></div>
          <div class="goalpath-row"><span class="goalpath-metric">Präsentationen</span><span class="goalpath-value">${fmtNum(teamPresentations, 0)}</span></div>
          <div class="goalpath-row"><span class="goalpath-metric">Deals</span><span class="goalpath-value">${fmtNum(teamDeals, 1)}</span></div>
        </div>
        <div class="goalpath-col">
          <div class="goalpath-col-header">Je Rep / Monat</div>
          <div class="goalpath-row"><span class="goalpath-metric">Kontakte</span><span class="goalpath-value">${repContacts}</span></div>
          <div class="goalpath-row"><span class="goalpath-metric">Discovery Calls</span><span class="goalpath-value">${repDiscovery}</span></div>
          <div class="goalpath-row"><span class="goalpath-metric">Präsentationen</span><span class="goalpath-value">${repPresentations}</span></div>
          <div class="goalpath-row"><span class="goalpath-metric">Deals</span><span class="goalpath-value">${repDeals}</span></div>
        </div>
      </div>
      <p style="font-size:8pt;color:#94a3b8;margin-top:6px;">
        Basis: Realistischer Pfad mit audit-adjustierten Conversion-Raten (C→D: ${fmtPct(gp.adjustedC2D)} | D→P: ${fmtPct(gp.adjustedD2P)} | P→Deal: ${fmtPct(gp.adjustedP2D)})
      </p>
      <div class="report-footer">Salesforge Sales-Report · ${companyName} · ${today()}</div>
    </div></div>`;
  }

  // ── 6. Top-5 Strategische Maßnahmen ──
  if (d.top5Recs && d.top5Recs.length > 0) {
    const rankColors = ['#1e3a8a', '#0f172a', '#4c1d95', '#064e3b', '#78350f'];
    const rankBg = ['#dbeafe', '#f1f5f9', '#ede9fe', '#d1fae5', '#fef3c7'];
    const rankTxt = ['#1e3a8a', '#334155', '#5b21b6', '#065f46', '#92400e'];

    d.top5Recs.forEach((rec, idx) => {
      const col = rankColors[idx] ?? '#1e3a8a';
      const bg = rankBg[idx] ?? '#dbeafe';
      const txt = rankTxt[idx] ?? '#1e3a8a';

      // Slide 1: Header, Situation, and first 3 measures
      html += `
    <div class="slide"><div class="slide-content">
    <div class="part-divider">
      <div class="part-divider-label">Kernstück</div>
      <h2>6. Die 5 strategischen Maßnahmen — Maßnahme ${idx + 1} (1/2)</h2>
    </div>
    <div style="border:1.5px solid #e2e8f0;border-radius:14px;margin-bottom:28px;overflow:hidden;">
      <!-- Rec Header -->
      <div style="background:linear-gradient(135deg,${col},${col}cc);padding:20px 24px;">
        <div style="display:flex;justify-content:space-between;align-items:flex-start;flex-wrap:wrap;gap:12px;">
          <div>
            <div style="font-size:7pt;font-weight:700;letter-spacing:2px;text-transform:uppercase;color:rgba(255,255,255,0.5);margin-bottom:4px;">Nr. ${rec.rank} &middot; ${rec.category}</div>
            <div style="font-size:13pt;font-weight:900;color:#fff;line-height:1.2;">${rec.headline || rec.title}</div>
            <div style="font-size:8.5pt;color:rgba(255,255,255,0.55);margin-top:5px;">${rec.timeframePhase} &middot; ${rec.timeframe}</div>
          </div>
          <div style="text-align:right;">
            <div style="font-size:7pt;text-transform:uppercase;letter-spacing:1px;color:rgba(255,255,255,0.45);margin-bottom:3px;">${rec.urgencyLabel}</div>
            <div style="font-size:14pt;font-weight:900;color:#fff;">${rec.urgencyValue}</div>
          </div>
        </div>
        ${rec.kpiContext ? `<div style="margin-top:12px;background:rgba(255,255,255,0.08);border:1px solid rgba(255,255,255,0.15);border-radius:8px;padding:8px 12px;font-size:8pt;color:rgba(255,255,255,0.6);">${rec.kpiContext}</div>` : ''}
      </div>

      <!-- Situation / Root Cause / Consequence -->
      <div style="padding:18px 24px;border-bottom:1px solid #f1f5f9;">
        <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:16px;">
          <div>
            <div style="font-size:7pt;font-weight:700;text-transform:uppercase;letter-spacing:1px;color:${txt};margin-bottom:5px;">Ausgangssituation</div>
            <div style="font-size:8.5pt;color:#334155;line-height:1.55;">${rec.situationText}</div>
          </div>
          <div>
            <div style="font-size:7pt;font-weight:700;text-transform:uppercase;letter-spacing:1px;color:${txt};margin-bottom:5px;">Ursache</div>
            <div style="font-size:8.5pt;color:#334155;line-height:1.55;">${rec.rootCauseText}</div>
          </div>
          <div>
            <div style="font-size:7pt;font-weight:700;text-transform:uppercase;letter-spacing:1px;color:${txt};margin-bottom:5px;">Konsequenz</div>
            <div style="font-size:8.5pt;color:#334155;line-height:1.55;">${rec.consequenceText}</div>
          </div>
        </div>
      </div>

      <!-- First 3 Maßnahmen -->
      <div style="padding:18px 24px;">
        <div style="font-size:8pt;font-weight:700;text-transform:uppercase;letter-spacing:1px;color:#0f172a;margin-bottom:12px;">Die 5 konkreten Maßnahmen (Teil 1)</div>
        ${rec.measures.slice(0, 3).map((m, mi) => `
        <div style="border:1px solid #e2e8f0;border-radius:10px;padding:14px 16px;margin-bottom:10px;background:#fafbfc;">
          <div style="display:flex;gap:10px;align-items:flex-start;">
            <div style="min-width:22px;height:22px;border-radius:50%;background:${bg};color:${txt};font-size:8pt;font-weight:800;display:flex;align-items:center;justify-content:center;flex-shrink:0;margin-top:1px;">${mi + 1}</div>
            <div style="flex:1;">
              <div style="display:flex;justify-content:space-between;align-items:flex-start;gap:8px;flex-wrap:wrap;margin-bottom:5px;">
                <div style="font-size:9.5pt;font-weight:700;color:#0f172a;">${m.title}</div>
                ${m.lever ? `<div style="font-size:7pt;font-weight:700;padding:2px 8px;border-radius:999px;background:${bg};color:${txt};white-space:nowrap;">${m.lever}</div>` : ''}
              </div>
              <div style="font-size:8.5pt;color:#475569;line-height:1.55;">${m.body}</div>
            </div>
          </div>
        </div>`).join('')}
      </div>
    </div>
    <div class="report-footer">Salesforge Sales-Report · ${companyName} · ${today()}</div>
    </div></div>`;

      // Slide 2: Remaining 2 measures and bottom section
      html += `
    <div class="slide"><div class="slide-content">
    <div class="part-divider">
      <div class="part-divider-label">Kernstück</div>
      <h2>6. Die 5 strategischen Maßnahmen — Maßnahme ${idx + 1} (2/2)</h2>
    </div>
    <div style="border:1.5px solid #e2e8f0;border-radius:14px;margin-bottom:28px;overflow:hidden;">
      <!-- Rec Header (Small) -->
      <div style="background:linear-gradient(135deg,${col},${col}cc);padding:12px 24px;">
        <div style="font-size:7pt;font-weight:700;letter-spacing:2px;text-transform:uppercase;color:rgba(255,255,255,0.5);margin-bottom:2px;">Nr. ${rec.rank} &middot; ${rec.category}</div>
        <div style="font-size:11pt;font-weight:900;color:#fff;line-height:1.2;">${rec.headline || rec.title}</div>
      </div>

      <!-- Remaining 2 Maßnahmen -->
      <div style="padding:18px 24px;">
        <div style="font-size:8pt;font-weight:700;text-transform:uppercase;letter-spacing:1px;color:#0f172a;margin-bottom:12px;">Die 5 konkreten Maßnahmen (Teil 2)</div>
        ${rec.measures.slice(3, 5).map((m, mi) => `
        <div style="border:1px solid #e2e8f0;border-radius:10px;padding:14px 16px;margin-bottom:10px;background:#fafbfc;">
          <div style="display:flex;gap:10px;align-items:flex-start;">
            <div style="min-width:22px;height:22px;border-radius:50%;background:${bg};color:${txt};font-size:8pt;font-weight:800;display:flex;align-items:center;justify-content:center;flex-shrink:0;margin-top:1px;">${mi + 4}</div>
            <div style="flex:1;">
              <div style="display:flex;justify-content:space-between;align-items:flex-start;gap:8px;flex-wrap:wrap;margin-bottom:5px;">
                <div style="font-size:9.5pt;font-weight:700;color:#0f172a;">${m.title}</div>
                ${m.lever ? `<div style="font-size:7pt;font-weight:700;padding:2px 8px;border-radius:999px;background:${bg};color:${txt};white-space:nowrap;">${m.lever}</div>` : ''}
              </div>
              <div style="font-size:8.5pt;color:#475569;line-height:1.55;">${m.body}</div>
            </div>
          </div>
        </div>`).join('')}
      </div>

      <!-- Erfolgskriterien + Why Now + First Step -->
      <div style="padding:14px 24px 18px;background:#f8fafc;border-top:1px solid #f1f5f9;">
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:16px;">
          <div>
            <div style="font-size:7pt;font-weight:700;text-transform:uppercase;letter-spacing:1px;color:#64748b;margin-bottom:8px;">Messbare Erfolgskriterien</div>
            ${rec.successCriteria.map(sc => `
            <div style="display:flex;gap:8px;padding:5px 0;border-bottom:1px solid #f1f5f9;">
              <div style="font-size:8pt;font-weight:700;color:#0f172a;min-width:120px;">${sc.metric}</div>
              <div style="font-size:8pt;color:#475569;">${sc.target}</div>
            </div>`).join('')}
          </div>
          <div>
            <div style="font-size:7pt;font-weight:700;text-transform:uppercase;letter-spacing:1px;color:#64748b;margin-bottom:8px;">Warum jetzt</div>
            <div style="font-size:8.5pt;color:#334155;line-height:1.5;margin-bottom:10px;">${rec.whyNow}</div>
            <div style="background:${bg};border:1px solid rgba(0,0,0,0.05);border-radius:8px;padding:8px 12px;">
              <div style="font-size:7pt;font-weight:700;color:${txt};margin-bottom:3px;">Erwarteter Impact</div>
              <div style="font-size:9pt;font-weight:700;color:${col};">${rec.impactStatement}</div>
            </div>
          </div>
        </div>
        <div style="margin-top:12px;background:#fff;border:1.5px solid ${col}33;border-left:4px solid ${col};border-radius:0 8px 8px 0;padding:10px 14px;">
          <div style="font-size:7pt;font-weight:700;text-transform:uppercase;letter-spacing:1px;color:${txt};margin-bottom:4px;">Erster Schritt heute</div>
          <div style="font-size:9pt;color:#0f172a;font-weight:600;">${rec.firstStep}</div>
        </div>
      </div>
    </div>
    <div class="report-footer">Salesforge Sales-Report · ${companyName} · ${today()}</div>
    </div></div>`;
    });
  }

  return html;
}

function buildTaskDetails(p: ReportPriorityCard, i: number): string {
  const tasks = p.taskDetails || [];
  if (tasks.length === 0) {
    // Fallback: show enrichedActions or actions as steps
    const actions = p.enrichedActions || p.actions;
    if (actions.length === 0) return '';
    return `
    <div class="priority-block">
      <div class="priority-block-label">Maßnahmen</div>
      <ul class="steps-list">
        ${actions.map((a, si) => `
          <li>
            <div class="step-num s${((si % 3) + 1)}">${si + 1}</div>
            <span>${a}</span>
          </li>`).join('')}
      </ul>
    </div>`;
  }

  return tasks.slice(0, 3).map((t, ti) => `
    <div class="task-card">
      <div class="task-title">${t.title}</div>
      <div class="task-desc">${t.description}</div>
      ${safeArr(t.steps).length > 0 ? `
        <ul class="steps-list">
          ${safeArr(t.steps).map((s, si) => `
            <li>
              <div class="step-num s${((si % 3) + 1)}">${si + 1}</div>
              <span>${s}</span>
            </li>`).join('')}
        </ul>` : ''}
      <div class="task-meta">
        <div class="task-meta-item"><strong>Owner:</strong> ${t.owner}</div>
        <div class="task-meta-item"><strong>Fällig in:</strong> ${t.dueInDays} Tagen</div>
        <div class="task-meta-item"><strong>Erfolgsmetrik:</strong> ${t.successMetric}</div>
        ${t.effort ? `<div class="task-meta-item"><strong>Aufwand:</strong> ${t.effort}</div>` : ''}
        ${t.impact ? `<div class="task-meta-item"><strong>Impact:</strong> ${t.impact}</div>` : ''}
      </div>
    </div>`).join('');
}

// ─── Part B: Ergebnisse ────────────────────────────────────────────────────────
function buildPartB(d: FullReportData): string {
  const r = d.result;
  let html = '';

  // ── 6. Umsatzrealität ──
  const m = r.computedMetrics;
  const tp = r.teamPerformance;
  const companyName = r.normalizedData?.companyName || 'Ihr Unternehmen';

  html += `
  <div class="slide"><div class="slide-content">
  <div class="part-divider">
    <div class="part-divider-label">Teil B</div>
    <h2>Audit-Ergebnisse — Vollständige Analyse</h2>
  </div>
  <h2>6. Ihre Umsatzrealität</h2>`;
  html += `<div class="kpi-grid-4">`;
  if (tp) {
    html += `
      <div class="kpi-card">
        <div class="kpi-label">Jahresziel</div>
        <div class="kpi-value">${fmtEur(tp.totalRevenueTargetYear)}</div>
        <div class="kpi-sub">laufendes Kalenderjahr</div>
      </div>
      <div class="kpi-card">
        <div class="kpi-label">Bisher erzielt (YTD)</div>
        <div class="kpi-value">${fmtEur(tp.revenueAchievedYTD)}</div>
        <div class="kpi-sub">tatsächlich realisiert</div>
      </div>
      <div class="kpi-card">
        <div class="kpi-label">Forecast-Gap</div>
        <div class="kpi-value red">${fmtEur(tp.projectedYearEndGap)}</div>
        <div class="kpi-sub">bei unveränderter Pace</div>
      </div>
      <div class="kpi-card">
        <div class="kpi-label">Pacing vs. Plan</div>
        <div class="kpi-value ${(tp.pacingVsPlanPct || 0) < 90 ? 'amber' : 'green'}">${fmtPct(tp.pacingVsPlanPct)}</div>
        <div class="kpi-sub">linearer Sollverlauf</div>
      </div>`;
  }
  if (m) {
    html += `
      <div class="kpi-card">
        <div class="kpi-label">Monatliche Umsatzlücke</div>
        <div class="kpi-value red">${fmtEur(m.monthlyRevenueGap)}</div>
        <div class="kpi-sub">vs. Benchmark-Conversion</div>
      </div>
      <div class="kpi-card">
        <div class="kpi-label">Jährliches Potenzial</div>
        <div class="kpi-value blue">${fmtEur(m.annualRevenueGap)}</div>
        <div class="kpi-sub">bei Benchmark-Performance</div>
      </div>
      <div class="kpi-card">
        <div class="kpi-label">Aktueller Monatsumsatz</div>
        <div class="kpi-value">${fmtEur(m.currentRevenue)}</div>
        <div class="kpi-sub">tatsächlich/Monat</div>
      </div>
      <div class="kpi-card">
        <div class="kpi-label">Potenzial-Umsatz</div>
        <div class="kpi-value green">${fmtEur(m.potentialRevenue)}</div>
        <div class="kpi-sub">bei optimierter Conversion</div>
      </div>`;
  }
  html += `<div class="report-footer">Salesforge Sales-Report · ${companyName} · ${today()}</div></div></div>`;

  // ── 7. Funnel Conversion-Raten ──
  if (m) {
    html += `<div class="slide"><div class="slide-content"><h2>7. Funnel Conversion-Raten</h2>`;
    html += `
    <table class="funnel-table">
      <thead>
        <tr>
          <th>Funnel-Stufe</th>
          <th>Ihr Wert</th>
          <th>Benchmark</th>
          <th>Status</th>
          <th>Lücke</th>
        </tr>
      </thead>
      <tbody>`;

    const steps = [
      {
        label: 'Kontakt → Discovery',
        cur: m.convContactToDiscovery,
        bench: m.benchmarkContactToDiscovery || 6,
      },
      {
        label: 'Discovery → Präsentation',
        cur: m.convDiscoveryToPresentation,
        bench: m.benchmarkDiscoveryToPresentation || 50,
      },
      {
        label: 'Präsentation → Deal',
        cur: m.convPresentationToDeal,
        bench: m.benchmarkPresentationToDeal || 28,
      },
    ];

    steps.forEach(({ label, cur, bench }) => {
      const ok = (cur || 0) >= bench * 0.85;
      const gap = Math.max(0, bench - (cur || 0));
      const statusCls = ok ? 'status-ok' : (gap > 15 ? 'status-crit' : 'status-warn');
      const statusText = ok ? '✓ Solide' : (gap > 15 ? '⚠ Kritisch' : '⚠ Lücke');
      html += `
        <tr>
          <td>${label}</td>
          <td>${fmtPct(cur)}</td>
          <td>${fmtPct(bench)}</td>
          <td class="${statusCls}">${statusText}</td>
          <td>${gap > 0 ? `${gap.toFixed(1)} pp` : '–'}</td>
        </tr>`;
    });

    html += `
        <tr class="total">
          <td>Kontakte pro Monat</td>
          <td colspan="4">${fmtNum(r.normalizedData?.monthlyContacts, 0)}</td>
        </tr>
      </tbody>
    </table>
    <div class="report-footer">Salesforge Sales-Report · ${companyName} · ${today()}</div>
    </div></div>`;
  }

  // ── 8. Top-Hebel ──
  const levers = safeArr(r.topLevers).slice(0, 3);
  if (levers.length > 0) {
    html += `<div class="slide"><div class="slide-content"><h2>8. Ihre Top-Umsatzhebel</h2>`;
    html += `<div class="kpi-grid-3">`;
    levers.forEach((lever, i) => {
      html += `
        <div class="kpi-card" style="border-color:${i === 0 ? '#fcd34d' : i === 1 ? '#93c5fd' : '#86efac'};">
          <div class="kpi-label">Hebel ${i + 1}</div>
          <div class="kpi-value small">${lever.title || '–'}</div>
          ${(lever.monthlyImpact || 0) > 0 ? `<div class="kpi-sub" style="color:#16a34a;font-weight:700;">${fmtEur(lever.monthlyImpact)}/Monat Potenzial</div>` : ''}
          <div class="kpi-sub" style="margin-top:6px;">${lever.whyItMatters || ''}</div>
        </div>`;
    });
    html += `</div><div class="report-footer">Salesforge Sales-Report · ${companyName} · ${today()}</div></div></div>`;
  }

  // ── 9. Strukturelle Ursachen ──
  const causes = safeArr(r.structuralCauses);
  if (causes.length > 0) {
    html += `
    <div class="slide"><div class="slide-content"><h2>9. Strukturelle Ursachen</h2>
    <p>Diese Faktoren erklären strukturell, warum das Ziel aktuell nicht stabil erreichbar ist.</p>`;
    causes.forEach((cause: any) => {
      const cls = cause.severity === 'high' ? 'cause-high' : cause.severity === 'good' ? 'cause-good' : 'cause-medium';
      const sevCls = cause.severity === 'high' ? 'sev-high' : cause.severity === 'good' ? 'sev-good' : 'sev-medium';
      const sevLabel = cause.severity === 'high' ? 'Kritisch' : cause.severity === 'good' ? 'Stark' : 'Mittel';
      html += `
      <div class="cause-card ${cls}">
        <div class="cause-header">
          <div class="cause-title">${cause.title || '–'}</div>
          <div class="cause-severity ${sevCls}">${sevLabel}</div>
        </div>
        ${cause.metricLabel && cause.metricValue ? `<div style="font-size:8.5pt;color:#64748b;margin-top:4px;">${cause.metricLabel}: <strong>${cause.metricValue}</strong></div>` : ''}
        ${cause.summary ? `<div class="cause-summary">${cause.summary}</div>` : ''}
        ${cause.businessImpact ? `<div class="cause-impact"><strong>Geschäftliche Folge:</strong> ${cause.businessImpact}</div>` : ''}
        ${cause.revenueImpactMonthly > 0 ? `<div style="font-size:8.5pt;font-weight:700;color:#16a34a;margin-top:4px;">~${fmtEur(cause.revenueImpactMonthly)}/Monat Potenzial</div>` : ''}
      </div>`;
    });
    html += `<div class="report-footer">Salesforge Sales-Report · ${companyName} · ${today()}</div></div></div>`;
  }

  // ── 10. Team-Performance ──
  if (tp) {
    html += `
    <div class="slide"><div class="slide-content"><h2>10. Team-Performance im Detail</h2>`;
    html += `<div class="kpi-grid-4">
      <div class="kpi-card">
        <div class="kpi-label">Produktive Reps</div>
        <div class="kpi-value">${tp.productiveReps}</div>
        <div class="kpi-sub">im Team</div>
      </div>
      <div class="kpi-card">
        <div class="kpi-label">Absolute Zielerreichung</div>
        <div class="kpi-value">${fmtPct(tp.absoluteGoalAttainmentPct)}</div>
        <div class="kpi-sub">YTD / Jahresziel</div>
      </div>
      <div class="kpi-card">
        <div class="kpi-label">Zielrückstand heute</div>
        <div class="kpi-value red">${fmtEur(tp.paceGapToDate)}</div>
        <div class="kpi-sub">Soll vs. Ist-Umsatz</div>
      </div>
      <div class="kpi-card">
        <div class="kpi-label">Rep Health Score</div>
        <div class="kpi-value ${(tp.repHealthScore || 0) < 60 ? 'red' : (tp.repHealthScore || 0) < 80 ? 'amber' : 'green'}">${tp.repHealthScore}/100</div>
        <div class="kpi-sub">Ziel, Pipeline, Execution</div>
      </div>
    </div>`;
    html += `<div class="kpi-grid-3" style="margin-top:12px;">
      <div class="kpi-card">
        <div class="kpi-label">Ziel / Rep / Jahr</div>
        <div class="kpi-value small">${fmtEur(tp.targetPerRepYear)}</div>
        <div class="kpi-sub">≈ ${fmtEur(tp.targetPerRepMonth)}/Monat</div>
      </div>
      <div class="kpi-card">
        <div class="kpi-label">Ist / Rep / Monat (Pace)</div>
        <div class="kpi-value small">${fmtEur(tp.actualRevenuePerRepMonthPaced)}</div>
        <div class="kpi-sub">hochgerechneter Durchschnitt</div>
      </div>
      <div class="kpi-card">
        <div class="kpi-label">Monatl. Aufholbedarf</div>
        <div class="kpi-value small amber">${fmtEur(tp.pacingRecoveryPotentialMonthly)}</div>
        <div class="kpi-sub">nötig für Jahresziel</div>
      </div>
    </div>`;

    // Summary Signals
    const signals = tp.summarySignals;
    if (signals) {
      const flags: Array<{ flag: boolean; icon: string; text: string }> = [
        { flag: signals.hasPacingProblem, icon: '⚠️', text: 'Das Team liegt unter dem linearen Sollverlauf. Ohne Gegensteuerung wird das Jahresziel voraussichtlich verfehlt.' },
        { flag: signals.hasTargetBreakdownProblem, icon: '⚠️', text: 'Das Umsatzziel ist nicht konsequent auf Reps heruntergebrochen. Das erschwert Steuerung und Review.' },
        { flag: signals.hasPipelineProblem, icon: '⚠️', text: 'Ein Teil der Reps verfügt nicht über ausreichend belastbare Pipeline. Strukturelles Risiko.' },
        { flag: signals.hasVarianceProblem, icon: '⚠️', text: 'Die Leistungsstreuung im Team ist hoch. Standardisierung von Top-Performer-Verhalten hebt den Durchschnitt.' },
        { flag: signals.hasReviewProblem, icon: '⚠️', text: 'Review-Rhythmus oder Steuerungsdisziplin deuten auf ein Management-Thema hin.' },
      ].filter(f => f.flag);
      if (flags.length > 0) {
        html += `<div style="background:#f8fafc;border:1px solid #e2e8f0;border-radius:10px;padding:14px;margin-top:12px;">
          <div style="font-size:9pt;font-weight:700;color:#0f172a;margin-bottom:8px;">Kritische Signale</div>`;
        flags.forEach(f => {
          html += `<div class="signal-flag"><div class="signal-icon">${f.icon}</div><div class="signal-text">${f.text}</div></div>`;
        });
        html += `</div>`;
      }
    }
    html += `<div class="report-footer">Salesforge Sales-Report · ${companyName} · ${today()}</div></div></div>`;
  }

  // ── 11. Kapazitäts-Check ──
  if (m?.capacityCheck && tp?.totalRevenueTargetYear > 0) {
    const cc = m.capacityCheck;
    const isUnreachable = cc.isTargetUnreachableWithCurrentSetup;
    const isHard = cc.isTargetUnreachableWithCurrentPerformance;
    html += `
    <div class="slide"><div class="slide-content">
      <h2>11. Kapazitäts-Check</h2>
      <div style="background:${isUnreachable ? '#fef2f2' : isHard ? '#fffbeb' : '#f0fdf4'};border:1.5px solid ${isUnreachable ? '#fca5a5' : isHard ? '#fcd34d' : '#86efac'};border-radius:12px;padding:16px;">
        <div style="font-size:10pt;font-weight:700;color:${isUnreachable ? '#991b1b' : isHard ? '#92400e' : '#14532d'};margin-bottom:8px;">
          ${isUnreachable ? '🚨 Ziel mit aktuellem Setup nicht erreichbar' : isHard ? '⚠️ Ziel nur mit besserem Execution erreichbar' : '✅ Ziel grundsätzlich erreichbar'}
        </div>
        <div style="display:grid;grid-template-columns:repeat(3,1fr);gap:12px;">
          <div><div style="font-size:8pt;color:#64748b;margin-bottom:3px;">Aktuelle Max-Kapazität</div><div style="font-size:13pt;font-weight:800;">${fmtEur(cc.currentMaxRevenueCapacity)}</div></div>
          <div><div style="font-size:8pt;color:#64748b;margin-bottom:3px;">Optimierte Max-Kapazität</div><div style="font-size:13pt;font-weight:800;">${fmtEur(cc.optimizedMaxRevenueCapacity)}</div></div>
          <div><div style="font-size:8pt;color:#64748b;margin-bottom:3px;">Jahres-Ziel</div><div style="font-size:13pt;font-weight:800;">${fmtEur(tp?.totalRevenueTargetYear)}</div></div>
        </div>
        ${cc.capacityGap > 0 ? `<div style="font-size:9pt;margin-top:8px;color:#64748b;">Kapazitätslücke: <strong style="color:#dc2626;">${fmtEur(cc.capacityGap)}</strong></div>` : ''}
      </div>
    </div>
    <div class="report-footer">Salesforge Sales-Report · ${companyName} · ${today()}</div>
    </div></div>`;
  }

  return html;
}

// ─── Full HTML Report ──────────────────────────────────────────────────────────
export function buildFullReportHtml(data: FullReportData): string {
  const companyName = data.result.normalizedData?.companyName || 'Ihr Unternehmen';
  return `<!DOCTYPE html>
<html lang="de">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1">
  <title>Salesforge Sales-Report — ${companyName}</title>
  <style>${buildStyles()}</style>
  <script>
    // Automatisch Druckdialog öffnen nach dem Laden
    window.addEventListener('load', function() {
      setTimeout(function() {
        window.print();
      }, 800);
    });
  <\/script>
</head>
<body>

  <!-- Toolbar (wird beim Drucken ausgeblendet) -->
  <div class="no-print" style="position:fixed;top:0;left:0;right:0;z-index:999;background:#0f172a;padding:12px 24px;display:flex;align-items:center;justify-content:space-between;box-shadow:0 2px 8px rgba(0,0,0,0.3);">
    <span style="color:rgba(255,255,255,0.7);font-size:13px;font-weight:600;">Salesforge · PDF-Report · ${companyName}</span>
    <div style="display:flex;gap:10px;">
      <button class="btn-print" onclick="window.print()">🖨️ Als PDF speichern</button>
      <button class="btn-close" onclick="window.close()">✕ Schließen</button>
    </div>
  </div>
  <div class="no-print" style="height:52px;"></div>

  ${buildCover(data)}
  ${buildPartA(data)}
  ${buildPartB(data)}

</body>
</html>`;
}

// ─── Download HTML Report (öffnet Druckdialog automatisch) ───────────────────
export function downloadFullReport(data: FullReportData): void {
  const html = buildFullReportHtml(data);
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const w = window.open(url, '_blank');
  if (!w) {
    // Popup blockiert — direkter HTML-Download als Fallback
    const a = document.createElement('a');
    a.href = url;
    const name = data.result.normalizedData?.companyName || 'Audit';
    a.download = `Salesforge_Report_${name.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.html`;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 300);
    return;
  }
  // Cleanup nach 5 Minuten
  setTimeout(() => URL.revokeObjectURL(url), 300_000);
}

// ═══════════════════════════════════════════════════════════════════════════════
// POWERPOINT GENERATOR (native XML/ZIP — no external dependencies)
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Generates a real PPTX file without any npm dependency.
 * PPTX = ZIP containing XML files.
 * We use the JSZip-free approach: build a minimal PPTX structure as a binary zip.
 * Since we can't use JSZip, we generate an HTML-based presentation that can be
 * exported/saved as a presentation, or we use a "download as CSV" approach for slides.
 *
 * ACTUAL APPROACH: We generate a real multi-slide HTML file styled like PowerPoint slides
 * (16:9, full-bleed, slide-per-page) that users can:
 *  1. Print as PDF (each page = 1 slide)
 *  2. Import into Google Slides via File > Import slides
 *  3. Use directly in browser presentation mode
 *
 * For a true PPTX, we build a minimal ZIP using a pure-JS zip encoder.
 */

// ── Minimal ZIP encoder ────────────────────────────────────────────────────────
// Based on the DEFLATE/ZIP spec, storing files uncompressed (method 0)
function uint32LE(n: number): number[] {
  return [n & 0xff, (n >> 8) & 0xff, (n >> 16) & 0xff, (n >> 24) & 0xff];
}
function uint16LE(n: number): number[] {
  return [n & 0xff, (n >> 8) & 0xff];
}

function strToBytes(s: string): Uint8Array {
  const enc = new TextEncoder();
  return enc.encode(s);
}

function crc32(data: Uint8Array): number {
  const table = new Int32Array(256);
  for (let i = 0; i < 256; i++) {
    let c = i;
    for (let j = 0; j < 8; j++) {
      c = (c & 1) ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    }
    table[i] = c;
  }
  let crc = 0xffffffff;
  for (let i = 0; i < data.length; i++) {
    crc = table[(crc ^ data[i]) & 0xff] ^ (crc >>> 8);
  }
  return (crc ^ 0xffffffff) >>> 0;
}

interface ZipFile {
  name: string;
  data: Uint8Array;
  offset: number;
}

function buildZip(files: Array<{ name: string; content: string }>): Uint8Array {
  const entries: ZipFile[] = [];
  const chunks: number[] = [];
  let offset = 0;

  for (const f of files) {
    const data = strToBytes(f.content);
    const nameBytes = strToBytes(f.name);
    const crc = crc32(data);
    const size = data.length;
    const nameLen = nameBytes.length;

    // Local file header
    const lfh = [
      0x50, 0x4b, 0x03, 0x04, // signature
      ...uint16LE(20),           // version needed
      ...uint16LE(0),            // flags
      ...uint16LE(0),            // compression: stored
      ...uint16LE(0),            // mod time
      ...uint16LE(0),            // mod date
      ...uint32LE(crc),
      ...uint32LE(size),
      ...uint32LE(size),
      ...uint16LE(nameLen),
      ...uint16LE(0),            // extra len
    ];
    chunks.push(...lfh, ...nameBytes, ...data);
    entries.push({ name: f.name, data, offset });
    offset += lfh.length + nameLen + size;
  }

  const cdOffset = offset;
  for (const e of entries) {
    const nameBytes = strToBytes(e.name);
    const crc = crc32(e.data);
    const size = e.data.length;
    const cde = [
      0x50, 0x4b, 0x01, 0x02, // central dir signature
      ...uint16LE(20),           // version made by
      ...uint16LE(20),           // version needed
      ...uint16LE(0),
      ...uint16LE(0),
      ...uint16LE(0),
      ...uint16LE(0),
      ...uint32LE(crc),
      ...uint32LE(size),
      ...uint32LE(size),
      ...uint16LE(nameBytes.length),
      ...uint16LE(0), ...uint16LE(0), ...uint16LE(0), ...uint16LE(0),
      ...uint32LE(0),
      ...uint32LE(e.offset),
      ...nameBytes,
    ];
    chunks.push(...cde);
  }

  const cdSize = chunks.length - cdOffset;
  const eocd = [
    0x50, 0x4b, 0x05, 0x06,
    0x00, 0x00, 0x00, 0x00,
    ...uint16LE(entries.length),
    ...uint16LE(entries.length),
    ...uint32LE(cdSize),
    ...uint32LE(cdOffset),
    0x00, 0x00,
  ];
  chunks.push(...eocd);

  return new Uint8Array(chunks);
}

// ── PPTX XML helpers ───────────────────────────────────────────────────────────
function escXml(s: string | null | undefined): string {
  return String(s || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

/** EMU: 1 inch = 914400 EMU, 1 cm = 360000 EMU */
const EMU_W = 9144000; // 25.4 cm = 10 inch slide width
const EMU_H = 5143500; // 14.29 cm = 5.63 inch slide height (16:9)

function txBody(
  text: string,
  opts: {
    bold?: boolean;
    size?: number;   // in pt * 100, e.g. 2400 = 24pt
    color?: string;  // hex rrggbb
    align?: 'l' | 'c' | 'r';
    wrap?: boolean;
    lineSpacing?: number;
  } = {}
): string {
  const { bold = false, size = 1400, color = '0f172a', align = 'l', wrap = true, lineSpacing } = opts;
  const lnSpc = lineSpacing ? `<a:lnSpc><a:spcPts val="${lineSpacing}"/></a:lnSpc>` : '';
  return `<p:txBody>
    <a:bodyPr wrap="${wrap ? 'square' : 'none'}" />
    <a:lstStyle/>
    <a:p>
      <a:pPr algn="${align}">${lnSpc}</a:pPr>
      <a:r>
        <a:rPr lang="de-DE" sz="${size}" b="${bold ? '1' : '0'}" dirty="0">
          <a:solidFill><a:srgbClr val="${color}"/></a:solidFill>
        </a:rPr>
        <a:t>${escXml(text)}</a:t>
      </a:r>
    </a:p>
  </p:txBody>`;
}

function multiLineTxBody(
  lines: Array<{ text: string; bold?: boolean; size?: number; color?: string }>,
  opts: { align?: 'l' | 'c' | 'r'; wrap?: boolean } = {}
): string {
  const { align = 'l', wrap = true } = opts;
  const paragraphs = lines.map(l => {
    const { text, bold = false, size = 1300, color = '334155' } = l;
    return `<a:p>
      <a:pPr algn="${align}"/>
      <a:r>
        <a:rPr lang="de-DE" sz="${size}" b="${bold ? '1' : '0'}" dirty="0">
          <a:solidFill><a:srgbClr val="${color}"/></a:solidFill>
        </a:rPr>
        <a:t>${escXml(text)}</a:t>
      </a:r>
    </a:p>`;
  }).join('');
  return `<p:txBody>
    <a:bodyPr wrap="${wrap ? 'square' : 'none'}" />
    <a:lstStyle/>
    ${paragraphs}
  </p:txBody>`;
}

function sp(
  id: number,
  x: number, y: number, w: number, h: number,
  fill: string | null,
  body: string,
  borderColor?: string
): string {
  const fillXml = fill
    ? `<a:solidFill><a:srgbClr val="${fill}"/></a:solidFill>`
    : `<a:noFill/>`;
  const border = borderColor
    ? `<a:ln><a:solidFill><a:srgbClr val="${borderColor}"/></a:solidFill></a:ln>`
    : `<a:ln><a:noFill/></a:ln>`;
  return `<p:sp>
    <p:nvSpPr>
      <p:cNvPr id="${id}" name="Shape${id}"/>
      <p:cNvSpPr><a:spLocks noGrp="1"/></p:cNvSpPr>
      <p:nvPr/>
    </p:nvSpPr>
    <p:spPr>
      <a:xfrm><a:off x="${x}" y="${y}"/><a:ext cx="${w}" cy="${h}"/></a:xfrm>
      <a:prstGeom prst="rect"><a:avLst/></a:prstGeom>
      ${fillXml}
      ${border}
    </p:spPr>
    ${body}
  </p:sp>`;
}

function buildSlide(spml: string): string {
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<p:sld xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main"
       xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"
       xmlns:p="http://schemas.openxmlformats.org/presentationml/2006/main"
       show="1">
  <p:cSld><p:spTree>
    <p:nvGrpSpPr><p:cNvPr id="1" name=""/><p:cNvGrpSpPr/><p:nvPr/></p:nvGrpSpPr>
    <p:grpSpPr><a:xfrm><a:off x="0" y="0"/><a:ext cx="0" cy="0"/><a:chOff x="0" y="0"/><a:chExt cx="0" cy="0"/></a:xfrm></p:grpSpPr>
    ${spml}
  </p:spTree></p:cSld>
  <p:clrMapOvr><a:masterClrMapping/></p:clrMapOvr>
</p:sld>`;
}

// slide dimensions helper
const W = EMU_W;
const H = EMU_H;
const M = 457200; // ~0.5cm margin in EMU

function cm(v: number) { return Math.round(v * 360000); }

// ── Individual Slides ──────────────────────────────────────────────────────────

function slideCover(data: FullReportData): string {
  const r = data.result;
  const name = r.normalizedData?.companyName || 'Ihr Unternehmen';
  const annualGap = (r.teamPerformance?.projectedYearEndGap || 0) > 0
    ? r.teamPerformance!.projectedYearEndGap
    : r.computedMetrics?.annualRevenueGap || 0;
  const monthlyLoss = annualGap > 0 ? Math.round(annualGap / 12) : r.computedMetrics?.monthlyRevenueGap || 0;

  const spml = [
    // dark background
    sp(2, 0, 0, W, H, '0f172a', '<p:txBody><a:bodyPr/><a:lstStyle/><a:p/></p:txBody>'),
    // Logo
    sp(3, M, M, cm(4), cm(0.6), null, txBody('SALESFORGE', { bold: true, size: 1000, color: '94a3b8' })),
    // Title
    sp(4, M, cm(2.5), cm(16), cm(2.2), null, txBody(`Sales-Maßnahmenplan\n${name}`, { bold: true, size: 3200, color: 'ffffff' })),
    // Subtitle
    sp(5, M, cm(5.2), cm(16), cm(0.8), null, txBody(`Audit-Vollreport · ${today()} · Vertraulich`, { size: 1200, color: '64748b' })),
    // KPI boxes
    sp(6, M, cm(7.0), cm(5), cm(2.2), '1e3a8a', multiLineTxBody([
      { text: 'MONATLICHER VERLUST', bold: true, size: 900, color: '94a3b8' },
      { text: fmtEur(monthlyLoss), bold: true, size: 2800, color: 'ffffff' },
      { text: 'aktuell nicht realisiert', size: 1000, color: '64748b' },
    ])),
    sp(7, cm(6), cm(7.0), cm(5), cm(2.2), '1e3a8a', multiLineTxBody([
      { text: 'JAHRES-ZIELLÜCKE', bold: true, size: 900, color: '94a3b8' },
      { text: fmtEur(annualGap), bold: true, size: 2800, color: 'ffffff' },
      { text: 'projiziert bis Jahresende', size: 1000, color: '64748b' },
    ])),
    sp(8, cm(12), cm(7.0), cm(5), cm(2.2), '1e3a8a', multiLineTxBody([
      { text: 'PRIORITÄT #1', bold: true, size: 900, color: '94a3b8' },
      { text: (safeArr(r.topLevers)[0]?.title || '–').substring(0, 30), bold: true, size: 1400, color: 'ffffff' },
      { text: `${r.teamPerformance?.productiveReps || r.normalizedData?.teamSize || '–'} aktive Reps`, size: 1000, color: '64748b' },
    ])),
  ].join('\n');
  return buildSlide(spml);
}

function slideExecutiveDiagnosis(data: FullReportData): string {
  const spml = [
    sp(2, 0, 0, W, cm(1.8), '1e3a8a', txBody('EXECUTIVE-DIAGNOSE', { bold: true, size: 1000, color: 'ffffff' })),
    sp(3, M, cm(2.2), W - 2 * M, cm(1.5), null,
      txBody(data.executiveDiagnosis.headline, { bold: true, size: 2000, color: '0f172a' })),
    sp(4, M, cm(3.9), W - 2 * M, cm(2.5), 'fef9c3',
      txBody(data.executiveDiagnosis.summary, { size: 1300, color: '78350f' }), 'fcd34d'),
    sp(5, M, cm(6.5), W - 2 * M, cm(1.2), '0f172a',
      txBody(data.signatureSentence, { size: 1200, color: 'e2e8f0' })),
  ].join('\n');
  return buildSlide(spml);
}

function slidePriority(p: ReportPriorityCard, data: FullReportData): string {
  const r = data.result;
  const bgColors = ['dbeafe', 'fef3c7', 'dcfce7'];
  const accentColors = ['1d4ed8', 'd97706', '059669'];
  const bg = bgColors[p.rank - 1] || 'f8fafc';
  const accent = accentColors[p.rank - 1] || '1e3a8a';

  const actions = (p.enrichedActions || p.actions).slice(0, 4);
  const actLines = actions.map((a, i) => `${i + 1}. ${a}`).join('\n');

  const spml = [
    sp(2, 0, 0, W, cm(1.8), accent, txBody(`PRIORITÄT ${p.rank} VON 3`, { bold: true, size: 1000, color: 'ffffff' })),
    sp(3, M, cm(2.1), W - 2 * M, cm(1.2), null, txBody(p.title, { bold: true, size: 2200, color: '0f172a' })),
    p.kpiContext ? sp(4, M, cm(3.4), cm(10), cm(0.6), 'f1f5f9', txBody(p.kpiContext, { size: 1100, color: '475569' })) : '',
    (p.revenueLeakMonthly || 0) > 0 ? sp(5, cm(11), cm(3.4), cm(6), cm(0.6), 'fef2f2', txBody(`Umsatzleck: ${fmtEur(p.revenueLeakMonthly)}/Monat`, { size: 1100, color: 'dc2626', bold: true }), 'fca5a5') : '',
    sp(6, M, cm(4.2), cm(8), cm(3.8), bg, multiLineTxBody([
      { text: 'PROBLEM', bold: true, size: 900, color: '64748b' },
      { text: '', size: 600, color: 'ffffff' },
      { text: p.problem.substring(0, 300), size: 1150, color: '334155' },
    ]), accent),
    sp(7, cm(9.2), cm(4.2), cm(8.3), cm(1.8), 'f8fafc', multiLineTxBody([
      { text: 'MASSNAHMEN', bold: true, size: 900, color: '64748b' },
      { text: '', size: 600, color: 'ffffff' },
      { text: actLines, size: 1100, color: '334155' },
    ])),
    sp(8, cm(9.2), cm(6.2), cm(8.3), cm(1.8), 'f0fdf4', multiLineTxBody([
      { text: 'KURZFRISTIGER IMPACT', bold: true, size: 900, color: '064e3b' },
      { text: '', size: 600, color: 'ffffff' },
      { text: p.shortTermImpact.substring(0, 200), size: 1100, color: '065f46' },
    ]), '86efac'),
  ].filter(Boolean).join('\n');
  return buildSlide(spml);
}

function slideQuickWins(data: FullReportData): string {
  const qws = data.quickWins.slice(0, 3);
  const boxW = Math.round((W - 4 * M) / 3);
  const colors = ['16a34a', '0284c7', '7c3aed'];
  const bgs = ['f0fdf4', 'eff6ff', 'faf5ff'];

  const spml = [
    sp(2, 0, 0, W, cm(1.8), '059669', txBody('QUICK WINS — SOFORT UMSETZBAR IN 14 TAGEN', { bold: true, size: 1000, color: 'ffffff' })),
    ...qws.map((qw, i) => {
      const x = M + i * (boxW + M);
      return sp(3 + i, x, cm(2.2), boxW, cm(5.2), bgs[i], multiLineTxBody([
        { text: `Quick Win ${i + 1}`, bold: true, size: 1000, color: colors[i] },
        { text: qw.title, bold: true, size: 1400, color: '0f172a' },
        { text: '', size: 600, color: 'ffffff' },
        { text: qw.whyItWorks.substring(0, 150), size: 1100, color: '334155' },
        { text: '', size: 600, color: 'ffffff' },
        { text: `Owner: ${qw.owner}`, size: 1000, color: '64748b' },
        { text: `Deadline: ${qw.deadline}`, size: 1000, color: '64748b' },
        qw.expectedImpact ? { text: `Effekt: ${qw.expectedImpact.substring(0, 80)}`, size: 1000, color: colors[i], bold: true } : { text: '', size: 600, color: 'ffffff' },
      ]), colors[i]);
    }),
  ].join('\n');
  return buildSlide(spml);
}

function slideRoadmap(data: FullReportData): string {
  const rm = data.roadmap;
  const phases = [
    { p: rm.phase_1_0_30, bg: 'fef2f2', accent: 'dc2626', label: '0–30 TAGE · STABILISIEREN' },
    { p: rm.phase_2_30_60, bg: 'fffbeb', accent: 'd97706', label: '30–60 TAGE · BESCHLEUNIGEN' },
    { p: rm.phase_3_60_90, bg: 'f0fdf4', accent: '059669', label: '60–90 TAGE · SKALIEREN' },
  ];
  const boxW = Math.round((W - 4 * M) / 3);

  const spml = [
    sp(2, 0, 0, W, cm(1.8), '0f172a', txBody('90-TAGE-FAHRPLAN', { bold: true, size: 1000, color: 'ffffff' })),
    ...phases.map(({ p, bg, accent, label }, i) => {
      const x = M + i * (boxW + M);
      const actions = safeArr(p.actions).slice(0, 4).map((a, j) => `${j + 1}. ${a}`).join('\n');
      return sp(3 + i, x, cm(2.2), boxW, cm(5.5), bg, multiLineTxBody([
        { text: label, bold: true, size: 900, color: accent },
        { text: p.goal, bold: true, size: 1300, color: '0f172a' },
        { text: `Fokus: ${p.focus}`, size: 1000, color: '64748b' },
        { text: '', size: 600, color: 'ffffff' },
        { text: actions, size: 1100, color: '334155' },
      ]), accent);
    }),
  ].join('\n');
  return buildSlide(spml);
}

function slideGoalPath(data: FullReportData): string {
  const r = data.result;
  const gp = r.computedMetrics?.goalPath;
  const reps = Number(r.teamPerformance?.productiveReps || r.normalizedData?.teamSize || 1);
  const safeReps = reps > 0 ? reps : 1;

  if (!gp || gp.remainingRevenueNeeded <= 0) return '';

  const teamC = gp.realisticRequiredContactsPerMonthFromNow || 0;
  const teamD = gp.realisticRequiredDiscoveryCallsPerMonthFromNow || 0;
  const teamP = gp.realisticRequiredPresentationsPerMonthFromNow || 0;
  const teamDeals = gp.requiredDealsPerMonthFromNow || 0;

  const spml = [
    sp(2, 0, 0, W, cm(1.8), '1e3a8a', txBody('OPERATIVE ZIELGRÖSSEN — WAS JETZT PASSIEREN MUSS', { bold: true, size: 1000, color: 'ffffff' })),
    sp(3, M, cm(2.2), W - 2 * M, cm(0.9), null, txBody(`Verbleibend: ${fmtEur(gp.remainingRevenueNeeded)} in ${gp.monthsRemainingApprox} Monaten · Konfidenz: ${gp.confidenceLabel || '–'}`, { size: 1200, color: '475569' })),
    // Gesamt-Team
    sp(4, M, cm(3.4), cm(7), cm(0.6), '1e3a8a', txBody('GESAMT-TEAM / MONAT', { bold: true, size: 900, color: 'ffffff' })),
    sp(5, M, cm(4.1), cm(7), cm(3.8), 'f8fafc', multiLineTxBody([
      { text: `Kontakte:          ${Math.ceil(teamC)}`, bold: true, size: 1500, color: '0f172a' },
      { text: `Discovery Calls:   ${Math.ceil(teamD)}`, bold: true, size: 1500, color: '0f172a' },
      { text: `Präsentationen:    ${Math.ceil(teamP)}`, bold: true, size: 1500, color: '0f172a' },
      { text: `Deals:             ${teamDeals.toFixed(1)}`, bold: true, size: 1500, color: '16a34a' },
    ]), 'e2e8f0'),
    // Je Rep
    sp(6, cm(9), cm(3.4), cm(8.3), cm(0.6), '059669', txBody('JE REP / MONAT', { bold: true, size: 900, color: 'ffffff' })),
    sp(7, cm(9), cm(4.1), cm(8.3), cm(3.8), 'f0fdf4', multiLineTxBody([
      { text: `Kontakte:          ${Math.ceil(teamC / safeReps)}`, bold: true, size: 1500, color: '0f172a' },
      { text: `Discovery Calls:   ${Math.ceil(teamD / safeReps)}`, bold: true, size: 1500, color: '0f172a' },
      { text: `Präsentationen:    ${Math.ceil(teamP / safeReps)}`, bold: true, size: 1500, color: '0f172a' },
      { text: `Deals:             ${(teamDeals / safeReps).toFixed(1)}`, bold: true, size: 1500, color: '16a34a' },
    ]), '86efac'),
    sp(8, M, cm(8.1), W - 2 * M, cm(0.6), null, txBody(
      `Basis: audit-adjustierte Conversion-Raten — C→D: ${fmtPct(gp.adjustedC2D)} | D→P: ${fmtPct(gp.adjustedD2P)} | P→Deal: ${fmtPct(gp.adjustedP2D)}`,
      { size: 950, color: '94a3b8' }
    )),
  ].join('\n');
  return buildSlide(spml);
}

function slideRevenueReality(data: FullReportData): string {
  const r = data.result;
  const tp = r.teamPerformance;
  const m = r.computedMetrics;
  const levers = safeArr(r.topLevers).slice(0, 3);

  const spml = [
    sp(2, 0, 0, W, cm(1.8), '0f172a', txBody('AUDIT-ERGEBNISSE — UMSATZREALITÄT', { bold: true, size: 1000, color: 'ffffff' })),
    // KPI row 1
    tp ? sp(3, M, cm(2.2), cm(4), cm(2.0), 'f8fafc', multiLineTxBody([
      { text: 'JAHRESZIEL', bold: true, size: 900, color: '64748b' },
      { text: fmtEur(tp.totalRevenueTargetYear), bold: true, size: 1800, color: '0f172a' },
    ]), 'e2e8f0') : '',
    tp ? sp(4, cm(5.5), cm(2.2), cm(4), cm(2.0), 'fef2f2', multiLineTxBody([
      { text: 'FORECAST-GAP', bold: true, size: 900, color: '7f1d1d' },
      { text: fmtEur(tp.projectedYearEndGap), bold: true, size: 1800, color: 'dc2626' },
    ]), 'fca5a5') : '',
    m ? sp(5, cm(10), cm(2.2), cm(4), cm(2.0), 'eff6ff', multiLineTxBody([
      { text: 'MONATL. POTENZIAL', bold: true, size: 900, color: '1e40af' },
      { text: fmtEur(m.monthlyRevenueGap), bold: true, size: 1800, color: '1d4ed8' },
    ]), '93c5fd') : '',
    m ? sp(6, cm(14.5), cm(2.2), cm(4), cm(2.0), 'f0fdf4', multiLineTxBody([
      { text: 'JAHRESPOTENZIAL', bold: true, size: 900, color: '064e3b' },
      { text: fmtEur(m.annualRevenueGap), bold: true, size: 1800, color: '16a34a' },
    ]), '86efac') : '',
    // Top Lever
    sp(7, M, cm(4.6), W - 2 * M, cm(0.6), '1e3a8a', txBody('TOP-UMSATZHEBEL', { bold: true, size: 900, color: 'ffffff' })),
    ...levers.map((l, i) => {
      const x = M + i * cm(6.2);
      return sp(8 + i, x, cm(5.4), cm(5.8), cm(2.3), 'fef9c3', multiLineTxBody([
        { text: `Hebel ${i + 1}`, bold: true, size: 900, color: '78350f' },
        { text: l.title || '–', bold: true, size: 1300, color: '0f172a' },
        (l.monthlyImpact || 0) > 0 ? { text: `${fmtEur(l.monthlyImpact)}/Monat`, bold: true, size: 1200, color: '16a34a' } : { text: '', size: 600, color: 'ffffff' },
      ]), 'fcd34d');
    }),
  ].filter(Boolean).join('\n');
  return buildSlide(spml);
}

function slideFunnelConversion(data: FullReportData): string {
  const r = data.result;
  const m = r.computedMetrics;
  if (!m) return '';

  const steps = [
    { label: 'Kontakt → Discovery', cur: m.convContactToDiscovery, bench: m.benchmarkContactToDiscovery || 30 },
    { label: 'Discovery → Präsentation', cur: m.convDiscoveryToPresentation, bench: m.benchmarkDiscoveryToPresentation || 65 },
    { label: 'Präsentation → Deal', cur: m.convPresentationToDeal, bench: m.benchmarkPresentationToDeal || 35 },
  ];

  const spml = [
    sp(2, 0, 0, W, cm(1.8), '1e3a8a', txBody('FUNNEL CONVERSION-RATEN', { bold: true, size: 1000, color: 'ffffff' })),
    ...steps.map(({ label, cur, bench }, i) => {
      const ok = (cur || 0) >= bench * 0.85;
      const gap = Math.max(0, bench - (cur || 0));
      const bg = ok ? 'f0fdf4' : (gap > 15 ? 'fef2f2' : 'fffbeb');
      const ac = ok ? '16a34a' : (gap > 15 ? 'dc2626' : 'd97706');
      return sp(3 + i, M, cm(2.4 + i * 2.0), W - 2 * M, cm(1.7), bg, multiLineTxBody([
        { text: label, bold: true, size: 1400, color: '0f172a' },
        { text: `Ihr Wert: ${fmtPct(cur)}   |   Benchmark: ${fmtPct(bench)}   |   ${ok ? '✓ Solide' : `⚠ Lücke: ${gap.toFixed(1)} pp`}`, size: 1200, color: ac, bold: !ok },
      ]), ac);
    }),
    sp(6, M, cm(8.5), W - 2 * M, cm(0.6), null, txBody(
      `Kontaktvolumen: ${fmtNum(r.normalizedData?.monthlyContacts, 0)}/Monat · Ø Dealsize: ${fmtEur(r.normalizedData?.averageDealSize)}`,
      { size: 1000, color: '94a3b8' }
    )),
  ].join('\n');
  return buildSlide(spml);
}

function slideTeamPerformance(data: FullReportData): string {
  const r = data.result;
  const tp = r.teamPerformance;
  if (!tp) return '';

  const signals = tp.summarySignals;
  const flags: string[] = [];
  if (signals?.hasPacingProblem) flags.push('⚠ Unter linearem Sollverlauf — Jahresziel gefährdet');
  if (signals?.hasTargetBreakdownProblem) flags.push('⚠ Ziele nicht auf Rep-Ebene heruntergebrochen');
  if (signals?.hasPipelineProblem) flags.push('⚠ Unzureichende Pipeline-Abdeckung');
  if (signals?.hasVarianceProblem) flags.push('⚠ Hohe Leistungsstreuung im Team');
  if (signals?.hasReviewProblem) flags.push('⚠ Review-Rhythmus verbesserungswürdig');
  const flagText = flags.length > 0 ? flags.join('\n') : '✓ Keine kritischen Signale';

  const spml = [
    sp(2, 0, 0, W, cm(1.8), '0f172a', txBody('TEAM-PERFORMANCE', { bold: true, size: 1000, color: 'ffffff' })),
    sp(3, M, cm(2.2), cm(4.5), cm(1.8), 'f8fafc', multiLineTxBody([
      { text: 'PRODUKTIVE REPS', bold: true, size: 900, color: '64748b' },
      { text: String(tp.productiveReps), bold: true, size: 3200, color: '0f172a' },
    ]), 'e2e8f0'),
    sp(4, cm(5.8), cm(2.2), cm(4.5), cm(1.8), 'fef2f2', multiLineTxBody([
      { text: 'ZIELRÜCKSTAND HEUTE', bold: true, size: 900, color: '991b1b' },
      { text: fmtEur(tp.paceGapToDate), bold: true, size: 1600, color: 'dc2626' },
    ]), 'fca5a5'),
    sp(5, cm(11.6), cm(2.2), cm(4.5), cm(1.8), 'f8fafc', multiLineTxBody([
      { text: 'REP HEALTH SCORE', bold: true, size: 900, color: '64748b' },
      { text: `${tp.repHealthScore}/100`, bold: true, size: 3200, color: (tp.repHealthScore || 0) < 60 ? 'dc2626' : (tp.repHealthScore || 0) < 80 ? 'd97706' : '16a34a' },
    ]), 'e2e8f0'),
    sp(6, cm(17.2), cm(2.2), cm(4.5), cm(1.8), 'f8fafc', multiLineTxBody([
      { text: 'PACING', bold: true, size: 900, color: '64748b' },
      { text: fmtPct(tp.pacingVsPlanPct), bold: true, size: 3200, color: (tp.pacingVsPlanPct || 0) < 90 ? 'd97706' : '16a34a' },
    ]), 'e2e8f0'),
    sp(7, M, cm(4.4), cm(10), cm(3.6), 'fef2f2', multiLineTxBody([
      { text: 'KRITISCHE SIGNALE', bold: true, size: 1000, color: '991b1b' },
      { text: '', size: 600, color: 'ffffff' },
      { text: flagText, size: 1200, color: '334155' },
    ]), 'fca5a5'),
    sp(8, cm(11), cm(4.4), cm(6.3), cm(1.5), 'f8fafc', multiLineTxBody([
      { text: `Ziel/Rep/Jahr: ${fmtEur(tp.targetPerRepYear)}`, size: 1200, color: '0f172a', bold: true },
      { text: `≈ ${fmtEur(tp.targetPerRepMonth)}/Monat`, size: 1100, color: '64748b' },
    ]), 'e2e8f0'),
    sp(9, cm(11), cm(6.1), cm(6.3), cm(1.5), 'f0fdf4', multiLineTxBody([
      { text: `Aufholbedarf: ${fmtEur(tp.pacingRecoveryPotentialMonthly)}/Monat`, size: 1200, color: '065f46', bold: true },
      { text: 'nötig für Jahresziel', size: 1100, color: '64748b' },
    ]), '86efac'),
    sp(10, M, cm(8.2), W - 2 * M, cm(0.6), null, txBody(
      `Hochgerechneter Jahresumsatz bei aktueller Pace: ${fmtEur(tp.projectedYearEndRevenue)} | Ziel: ${fmtEur(tp.totalRevenueTargetYear)}`,
      { size: 950, color: '94a3b8' }
    )),
  ].join('\n');
  return buildSlide(spml);
}

// ─── Build PPTX ───────────────────────────────────────────────────────────────
export function buildPptx(data: FullReportData): Uint8Array {
  const slides = [
    slideCover(data),
    slideExecutiveDiagnosis(data),
    ...data.priorities.map(p => slidePriority(p, data)),
    slideQuickWins(data),
    slideRoadmap(data),
    slideGoalPath(data),
    slideRevenueReality(data),
    slideFunnelConversion(data),
    slideTeamPerformance(data),
  ].filter(Boolean);

  const slideCount = slides.length;

  // ── _rels/.rels ──
  const relsRoot = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="ppt/presentation.xml"/>
</Relationships>`;

  // ── [Content_Types].xml ──
  const slideContentTypes = slides.map((_, i) =>
    `<Override PartName="/ppt/slides/slide${i + 1}.xml" ContentType="application/vnd.openxmlformats-officedocument.presentationml.slide+xml"/>`
  ).join('\n');

  const contentTypes = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
  <Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
  <Default Extension="xml" ContentType="application/xml"/>
  <Override PartName="/ppt/presentation.xml" ContentType="application/vnd.openxmlformats-officedocument.presentationml.presentation.main+xml"/>
  <Override PartName="/ppt/slideMasters/slideMaster1.xml" ContentType="application/vnd.openxmlformats-officedocument.presentationml.slideMaster+xml"/>
  <Override PartName="/ppt/slideLayouts/slideLayout1.xml" ContentType="application/vnd.openxmlformats-officedocument.presentationml.slideLayout+xml"/>
  ${slideContentTypes}
</Types>`;

  // ── ppt/presentation.xml ──
  const slideIds = slides.map((_, i) =>
    `<p:sldId id="${256 + i}" r:id="rId${i + 1}"/>`
  ).join('\n');

  const presentation = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<p:presentation xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main"
                xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"
                xmlns:p="http://schemas.openxmlformats.org/presentationml/2006/main"
                saveSubsetFonts="1">
  <p:sldMasterIdLst><p:sldMasterId id="2147483648" r:id="rMaster1"/></p:sldMasterIdLst>
  <p:sldIdLst>${slideIds}</p:sldIdLst>
  <p:sldSz cx="${EMU_W}" cy="${EMU_H}" type="screen16x9"/>
  <p:notesSz cx="${EMU_H}" cy="${EMU_W}"/>
</p:presentation>`;

  // ── ppt/_rels/presentation.xml.rels ──
  const slideRels = slides.map((_, i) =>
    `<Relationship Id="rId${i + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/slide" Target="slides/slide${i + 1}.xml"/>`
  ).join('\n');

  const presentationRels = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  ${slideRels}
  <Relationship Id="rMaster1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/slideMaster" Target="slideMasters/slideMaster1.xml"/>
</Relationships>`;

  // ── Minimal slideMaster ──
  const slideMaster = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<p:sldMaster xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main"
             xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"
             xmlns:p="http://schemas.openxmlformats.org/presentationml/2006/main">
  <p:cSld><p:spTree>
    <p:nvGrpSpPr><p:cNvPr id="1" name=""/><p:cNvGrpSpPr/><p:nvPr/></p:nvGrpSpPr>
    <p:grpSpPr><a:xfrm><a:off x="0" y="0"/><a:ext cx="0" cy="0"/><a:chOff x="0" y="0"/><a:chExt cx="0" cy="0"/></a:xfrm></p:grpSpPr>
  </p:spTree></p:cSld>
  <p:clrMap bg1="lt1" tx1="dk1" bg2="lt2" tx2="dk2" accent1="accent1" accent2="accent2" accent3="accent3" accent4="accent4" accent5="accent5" accent6="accent6" hlink="hlink" folHlink="folHlink"/>
  <p:sldLayoutIdLst><p:sldLayoutId id="2147483649" r:id="rLayout1"/></p:sldLayoutIdLst>
</p:sldMaster>`;

  const slideMasterRels = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rLayout1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/slideLayout" Target="../slideLayouts/slideLayout1.xml"/>
</Relationships>`;

  const slideLayout = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<p:sldLayout xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main"
             xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"
             xmlns:p="http://schemas.openxmlformats.org/presentationml/2006/main"
             type="blank" preserve="1">
  <p:cSld name="Blank"><p:spTree>
    <p:nvGrpSpPr><p:cNvPr id="1" name=""/><p:cNvGrpSpPr/><p:nvPr/></p:nvGrpSpPr>
    <p:grpSpPr><a:xfrm><a:off x="0" y="0"/><a:ext cx="0" cy="0"/><a:chOff x="0" y="0"/><a:chExt cx="0" cy="0"/></a:xfrm></p:grpSpPr>
  </p:spTree></p:cSld>
  <p:clrMapOvr><a:masterClrMapping/></p:clrMapOvr>
</p:sldLayout>`;

  const slideLayoutRels = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rMaster1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/slideMaster" Target="../slideMasters/slideMaster1.xml"/>
</Relationships>`;

  // ── per-slide rels ──
  const perSlideRels = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/slideLayout" Target="../slideLayouts/slideLayout1.xml"/>
</Relationships>`;

  // ── Assemble ZIP files ──
  const zipFiles: Array<{ name: string; content: string }> = [
    { name: '[Content_Types].xml', content: contentTypes },
    { name: '_rels/.rels', content: relsRoot },
    { name: 'ppt/presentation.xml', content: presentation },
    { name: 'ppt/_rels/presentation.xml.rels', content: presentationRels },
    { name: 'ppt/slideMasters/slideMaster1.xml', content: slideMaster },
    { name: 'ppt/slideMasters/_rels/slideMaster1.xml.rels', content: slideMasterRels },
    { name: 'ppt/slideLayouts/slideLayout1.xml', content: slideLayout },
    { name: 'ppt/slideLayouts/_rels/slideLayout1.xml.rels', content: slideLayoutRels },
    ...slides.map((xml, i) => ({ name: `ppt/slides/slide${i + 1}.xml`, content: xml })),
    ...slides.map((_, i) => ({ name: `ppt/slides/_rels/slide${i + 1}.xml.rels`, content: perSlideRels })),
  ];

  return buildZip(zipFiles);
}

// ─── Download PPTX ────────────────────────────────────────────────────────────
export function downloadPptx(data: FullReportData): void {
  const bytes = buildPptx(data);
  const blob = new Blob([bytes.buffer as ArrayBuffer], { type: 'application/vnd.openxmlformats-officedocument.presentationml.presentation' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  const name = data.result.normalizedData?.companyName || 'Audit';
  a.download = `Salesforge_Praesentation_${name.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pptx`;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 200);
}